import React from 'react'
import ReactDOM from 'react-dom'

import { ConfirmationModal, ModalActionTypes } from '@acorns/web-components'
import { ThemeProvider } from 'styled-components'

import ThemeObject from 'theme'
import {
  CANCEL_NAVIGATION_BUTTON,
  NAVIGATE_ANYWAY_BUTTON,
} from 'utils/testing/testIDs'

import { routes } from './routes'

export enum NavigationPreventionModals {
  personal = 'personal',
  recurringExit = 'recurringExit',
  earlyOnboardingExitWarning = 'earlyOnboardingExitWarning',
}

/**
 * @description This method is called when:
 *  - User does something that fires any of the methods on the `history` object (goBack, go, push, etc.)
 *  - Prompt (from react-router-dom) is in the render
 *  - Prompt's `when` prop is true.
 * @see https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/BrowserRouter.md#getuserconfirmation-func
 * @param {string} message should match case within internal, enum-based switch-case
 * @param {(callbackState: boolean) => void} callback if true, navigation continues as planned - otherwise, it's cancelled outright
 */
export const getUserConfirmation = (
  message: string,
  callback: (callbackState: boolean) => void,
) => {
  const container = document.createElement('div')
  container.setAttribute('custom-confirmation-navigation', '')
  document.body.appendChild(container)

  const closeModal = () => ReactDOM.unmountComponentAtNode(container)

  const closeModalAndNavigate = ({
    shouldNavigateAsPlanned,
  }: {
    shouldNavigateAsPlanned: boolean
  }) => {
    closeModal()
    callback(shouldNavigateAsPlanned)
  }

  const navigateAnyway = () =>
    closeModalAndNavigate({ shouldNavigateAsPlanned: true })
  const cancelNavigation = () =>
    closeModalAndNavigate({ shouldNavigateAsPlanned: false })

  const commonProps = {
    isOpen: true,
    isTrapModal: true,
    onRequestClose: cancelNavigation,
  }

  let modal: undefined | JSX.Element
  switch (message) {
    case NavigationPreventionModals.personal:
      modal = (
        <ConfirmationModal
          actions={[
            {
              handler: navigateAnyway,
              label: 'Leave',
              testId: NAVIGATE_ANYWAY_BUTTON,
              type: ModalActionTypes.danger,
            },
            {
              handler: cancelNavigation,
              label: 'Stay',
              testId: CANCEL_NAVIGATION_BUTTON,
              type: ModalActionTypes.primary,
            },
          ]}
          message="You'll lose any changes you've made unless you save them."
          title="Are you sure?"
          {...commonProps}
        />
      )
      break

    case NavigationPreventionModals.recurringExit:
      modal = (
        <ConfirmationModal
          actions={[
            {
              handler: cancelNavigation,
              label: 'Never mind',
              testId: CANCEL_NAVIGATION_BUTTON,
              type: ModalActionTypes.secondary,
            },
            {
              handler: navigateAnyway,
              label: "I'm sure",
              testId: NAVIGATE_ANYWAY_BUTTON,
              type: ModalActionTypes.primary,
            },
          ]}
          message="Any changes you made won't be saved."
          title="Are you sure?"
          {...commonProps}
        />
      )
      break

    case NavigationPreventionModals.earlyOnboardingExitWarning:
      modal = (
        <ConfirmationModal
          actions={[
            {
              handler: cancelNavigation,
              label: 'Never mind',
              testId: CANCEL_NAVIGATION_BUTTON,
              type: ModalActionTypes.secondary,
            },
            {
              handler: () => {
                window.location.replace(routes.early.index)
              },
              label: 'Exit',
              testId: NAVIGATE_ANYWAY_BUTTON,
              type: ModalActionTypes.danger,
            },
          ]}
          message="If you exit now, this Early Invest account will be incomplete, and you will not be able to invest in it."
          title="Are you sure you want to exit?"
          {...commonProps}
        />
      )
      break
  }

  if (!modal) {
    return callback(true)
  }

  ReactDOM.render(
    <ThemeProvider theme={ThemeObject}>{modal}</ThemeProvider>,
    container,
  )
}
